import { useMemo, useState } from 'react';
import { Icon } from '@ascend/shared/components';
import {
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';
import {
  ADMIN_DASHBOARD_FACILITIES,
  ADMIN_KYC_CASES,
  ADMIN_LIQUIDATION_HISTORY,
  ADMIN_MARGIN_ACCOUNTS,
  getFacilityNameByAccountId,
} from '../data/mockAdapter';

type StatusFilter = 'all' | 'settled' | 'pending';

interface TransactionRecord {
  id: string;
  accountId: string;
  facilityName: string;
  actionType: 'auction_resolution' | 'freeze' | 'pause' | 'blacklist';
  actionLabel: string;
  details: string[];
  note: string;
  status: 'settled' | 'pending';
  timestamp: string;
  timestampMs: number;
  txHash: string;
}

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const BASE_TIMESTAMP_MS = Date.UTC(2026, 2, 24, 14, 30, 0);

function formatTimestamp(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hour}:${minute}`;
}

function buildTxHash(accountId: string, index: number): string {
  const compact = accountId.replace(/[^a-z0-9]/gi, '').slice(-4).toLowerCase().padStart(4, '0');
  const head = (9000 + index).toString(16);
  const tail = (12000 + index).toString(16);
  return `0x${compact}${head}...${tail}`;
}

export function TransactionHistoryPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const {
    query,
    setQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    resetPage,
  } = useTableControls(TABLE_PAGE_SIZE_OPTIONS[0]);

  const liquidationTransactions = useMemo<TransactionRecord[]>(
    () =>
      ADMIN_LIQUIDATION_HISTORY.map((record, index) => {
        const timestampMs = BASE_TIMESTAMP_MS - index * 6 * 60 * 60 * 1000;
        return {
          id: `tx-${String(index + 1).padStart(3, '0')}`,
          accountId: record.account,
          facilityName: getFacilityNameByAccountId(record.account),
          actionType: 'auction_resolution' as const,
          actionLabel: 'Auction Resolution',
          details: [
            `Reserve ${record.reserveAdvance}`,
            `Proceeds ${record.proceeds}`,
            `Variance ${record.variance}`,
          ],
          note: record.auctionStatus === 'settled'
            ? 'Liquidation auction settled'
            : 'Liquidation auction in progress',
          status: (record.finalStatus === 'Settled' ? 'settled' : 'pending') as 'settled' | 'pending',
          timestamp: formatTimestamp(new Date(timestampMs)),
          timestampMs,
          txHash: buildTxHash(record.account, index),
        };
      }).sort((a, b) => b.timestampMs - a.timestampMs),
    [],
  );

  const complianceTransactions = useMemo<TransactionRecord[]>(() => {
    const baseOffset = ADMIN_LIQUIDATION_HISTORY.length + 1;

    const facilities = ADMIN_DASHBOARD_FACILITIES.filter(
      (facility) => facility.status === 'paused' || facility.status === 'shutdown',
    );
    const accounts = ADMIN_MARGIN_ACCOUNTS.filter(
      (account) => account.status === 'frozen' || account.status === 'paused',
    );
    const kycRejections = ADMIN_KYC_CASES.filter(
      (kycCase) => kycCase.reviewStatus === 'rejected',
    );

    const facilityActions = facilities.map((facility, idx) => {
      const offset = baseOffset + idx;
      const timestampMs = BASE_TIMESTAMP_MS - offset * 6 * 60 * 60 * 1000;
      return {
        id: `tx-compliance-facility-${facility.id}`,
        accountId: facility.id,
        facilityName: facility.name,
        actionType: 'pause' as const,
        actionLabel: facility.status === 'shutdown' ? 'Facility Shutdown' : 'Facility Pause',
        details: [facility.status === 'shutdown' ? 'State: shutdown' : 'State: paused'],
        note: facility.status === 'shutdown'
          ? 'Facility moved to shutdown state'
          : 'Facility temporarily paused',
        status: 'settled' as const,
        timestamp: formatTimestamp(new Date(timestampMs)),
        timestampMs,
        txHash: buildTxHash(facility.id, offset + 1),
      };
    });

    const accountActions = accounts.map((account, idx) => {
      const offset = baseOffset + facilities.length + idx;
      const timestampMs = BASE_TIMESTAMP_MS - offset * 6 * 60 * 60 * 1000;
      const isFrozen = account.status === 'frozen';
      return {
        id: `tx-compliance-account-${account.id}`,
        accountId: account.id,
        facilityName: getFacilityNameByAccountId(account.id),
        actionType: isFrozen ? 'freeze' as const : 'pause' as const,
        actionLabel: isFrozen ? 'Account Freeze' : 'Account Pause',
        details: [`Health Factor: ${account.healthFactor.toFixed(2)}`],
        note: isFrozen
          ? 'Account frozen due to compliance risk controls'
          : 'Account paused pending operational review',
        status: 'settled' as const,
        timestamp: formatTimestamp(new Date(timestampMs)),
        timestampMs,
        txHash: buildTxHash(account.id, offset + 1),
      };
    });

    const blacklistActions = kycRejections.map((kycCase, idx) => {
      const offset = baseOffset + facilities.length + accounts.length + idx;
      const timestampMs = BASE_TIMESTAMP_MS - offset * 6 * 60 * 60 * 1000;
      return {
        id: `tx-compliance-blacklist-${kycCase.id}`,
        accountId: kycCase.walletAddress,
        facilityName: kycCase.facilityName,
        actionType: 'blacklist' as const,
        actionLabel: 'Wallet Blacklist',
        details: [`Risk Score: ${kycCase.riskScore}`],
        note: `KYC rejected for ${kycCase.name}; wallet restricted`,
        status: 'settled' as const,
        timestamp: formatTimestamp(new Date(timestampMs)),
        timestampMs,
        txHash: buildTxHash(kycCase.id, offset + 1),
      };
    });

    return [...facilityActions, ...accountActions, ...blacklistActions];
  }, []);

  const transactions = useMemo(
    () => [...liquidationTransactions, ...complianceTransactions].sort((a, b) => b.timestampMs - a.timestampMs),
    [complianceTransactions, liquidationTransactions],
  );

  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    if (statusFilter !== 'all') {
      result = result.filter((row) => row.status === statusFilter);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
          result = result.filter(
            (row) =>
              row.accountId.toLowerCase().includes(q)
              || row.facilityName.toLowerCase().includes(q)
              || row.actionLabel.toLowerCase().includes(q)
              || row.note.toLowerCase().includes(q)
              || row.details.some((detail) => detail.toLowerCase().includes(q))
              || row.txHash.toLowerCase().includes(q),
          );
    }

    return result;
  }, [query, statusFilter, transactions]);

  const { rows: paginatedRows } = usePaginatedRows(filteredTransactions, page, pageSize);

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Transaction History</h1>
          <p className="text-sm text-muted-foreground">
            Facility liquidation settlement and reserve reconciliation records.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value as StatusFilter);
            resetPage();
          }}
          className={`${INPUT_CLASS} sm:w-44`}
        >
          <option value="all">All Status</option>
          <option value="settled">Settled</option>
          <option value="pending">Pending</option>
        </select>
        <div className="sm:ml-auto">
          <SearchBar
            value={query}
            onChange={(value) => {
              setQuery(value);
              resetPage();
            }}
            placeholder="Search account, facility, or tx hash..."
            className="w-full sm:w-72"
          />
        </div>
      </div>

      <div className="space-y-3">
        {paginatedRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">No transaction records found.</p>
          </div>
        ) : (
          paginatedRows.map((record) => {
            const actionIconName = record.actionType === 'freeze'
              ? 'streamline:snow-flake'
              : record.actionType === 'pause'
                ? 'streamline:entertainment-control-button-pause-1-button-television-buttons-movies-tv-pause-video-controls'
                : record.actionType === 'blacklist'
                  ? 'streamline:shield-2'
                  : 'streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court';
            return (
              <div
                key={record.id}
                className="rounded-xl border border-border p-4 hover:bg-accent/30 transition-colors"
              >
                <div className="hidden md:grid grid-cols-[1.6fr_0.9fr] gap-4 items-center">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon name={actionIconName} className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium truncate">
                        {record.actionLabel} - {record.accountId}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {record.facilityName} - {record.timestamp}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{record.note}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {record.details.map((detail) => (
                        <span
                          key={`${record.id}-${detail}`}
                          className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {detail}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="justify-self-end text-right">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      record.status === 'settled'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {record.status === 'settled' ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3 w-3" /> : <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3 w-3" />}
                      {record.status}
                    </span>
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">{record.txHash}</p>
                  </div>
                </div>

                <div className="md:hidden space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{record.actionLabel}</p>
                      <p className="font-mono text-xs text-muted-foreground truncate">{record.accountId}</p>
                      <p className="text-xs text-muted-foreground truncate">{record.facilityName}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      record.status === 'settled'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {record.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {record.details.map((detail) => (
                      <span
                        key={`${record.id}-mobile-${detail}`}
                        className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                      >
                        {detail}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{record.note}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{record.timestamp}</span>
                    <span className="font-mono">{record.txHash}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filteredTransactions.length}
        label="transaction records"
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}
